import {
  PSQI_COMPONENT_METADATA,
  PSQI_DAYTIME_PROBLEM_OPTIONS,
  PSQI_DISTURBANCE_ITEMS,
  PSQI_FREQUENCY_OPTIONS,
  PSQI_PARTNER_OBSERVATION_ITEMS,
  PSQI_PARTNER_STATUS_OPTIONS,
  PSQI_SUBJECTIVE_QUALITY_OPTIONS,
  getPSQIClassification,
  normalizePSQIPayload,
  type PSQIComponentScores,
  type PSQIResultPayload,
} from '../tests/PSQITest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

const getOptionLabel = (
  options: ReadonlyArray<{ value: number | string; label: string }>,
  value: number | string | null | undefined,
): string => options.find((option) => option.value === value)?.label ?? 'Não informado';

const formatHourValue = (value?: number | null): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1).replace('.', ',')} h` : '—';

const formatPercentValue = (value?: number | null): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1).replace('.', ',')}%` : '—';

const getTopComponents = (components: PSQIComponentScores): Array<{ key: keyof PSQIComponentScores; label: string; score: number }> =>
  PSQI_COMPONENT_METADATA.map((item) => ({
    key: item.key,
    label: item.label,
    score: components[item.key],
  }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

const getComponentDetail = (payload: PSQIResultPayload, key: keyof PSQIComponentScores): string => {
  switch (key) {
    case 'subjectiveQuality':
      return getOptionLabel(PSQI_SUBJECTIVE_QUALITY_OPTIONS, payload.subjectiveQuality);
    case 'sleepLatency':
      return `${payload.sleepLatencyMinutes} min para dormir e frequência de dormir após 30 min`;
    case 'sleepDuration':
      return `${formatHourValue(payload.sleepDurationHours)} por noite`;
    case 'habitualSleepEfficiency':
      return `${formatPercentValue(payload.sleepEfficiencyPercent)} (${formatHourValue(payload.timeInBedHours)} na cama)`;
    case 'sleepDisturbances':
      return 'Soma dos eventos 5b a 5j';
    case 'sleepMedicationUse':
      return getOptionLabel(PSQI_FREQUENCY_OPTIONS, payload.sleepMedicationUse);
    case 'daytimeDysfunction':
      return 'Soma da sonolência diurna e da perda de entusiasmo';
    default:
      return '—';
  }
};

export const getPSQISeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  return getPSQIClassification(total);
};

export const getPSQISeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  if (total <= 5) return 'bg-emerald-100 text-emerald-700';
  return 'bg-rose-100 text-rose-700';
};

export const getPSQIScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  if (total <= 5) return '#10b981';
  return '#f43f5e';
};

export const getPSQIInterpretationText = (payload?: PSQIResultPayload | null): string => {
  if (!payload) {
    return 'Não há dados suficientes para interpretar o PSQI.';
  }

  const topComponents = getTopComponents(payload.components).slice(0, 2);
  const prominentDomains =
    topComponents.length > 0
      ? `Os componentes mais comprometidos foram ${topComponents.map((item) => `${item.label.toLowerCase()} (${item.score}/3)`).join(' e ')}.`
      : 'Nenhum componente apresentou alteração relevante.';

  if (payload.total <= 5) {
    return `O escore global do PSQI foi ${payload.total}/21, compatível com boa qualidade do sono no último mês. ${prominentDomains}`;
  }

  return `O escore global do PSQI foi ${payload.total}/21, acima do ponto de corte clássico (>5), sugerindo má qualidade do sono no último mês. ${prominentDomains}`;
};

interface PSQIResultRendererProps {
  payload: unknown;
}

export default function PSQIResultRenderer({ payload }: PSQIResultRendererProps) {
  const normalized = normalizePSQIPayload(payload);

  if (!normalized) {
    return (
      <div className="rounded-[24px] border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
        Não há dados suficientes para exibir o resultado do PSQI.
      </div>
    );
  }

  const interpretation = resolveAssessmentInterpretationText(normalized, getPSQIInterpretationText(normalized));
  const topComponents = getTopComponents(normalized.components);
  const hasPartnerNotes =
    normalized.partnerStatus !== 'none' &&
    (PSQI_PARTNER_OBSERVATION_ITEMS.some((item) => normalized.partnerObservations[item.key] != null) ||
      normalized.partnerObservations.otherRestlessnessText.trim().length > 0);

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação PSQI"
        value={normalized.total}
        valueColor={getPSQIScoreColor(normalized.total)}
        valueCaption={`${normalized.total}/21`}
        classification={getPSQISeverityLabel(normalized.total)}
        supportingText="Escores acima de 5 sugerem pior qualidade do sono."
        interpretation={interpretation}
      >
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">Eficiência do sono</div>
              <div className="mt-3 text-lg font-bold text-slate-900">{formatPercentValue(normalized.sleepEfficiencyPercent)}</div>
              <div className="mt-2 text-sm text-slate-500">Tempo dormido em relação ao tempo na cama.</div>
            </div>
            <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">Tempo na cama</div>
              <div className="mt-3 text-lg font-bold text-slate-900">{formatHourValue(normalized.timeInBedHours)}</div>
              <div className="mt-2 text-sm text-slate-500">Calculado entre o horário de deitar e levantar.</div>
            </div>
            <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">Latência</div>
              <div className="mt-3 text-lg font-bold text-slate-900">{normalized.sleepLatencyMinutes} min</div>
              <div className="mt-2 text-sm text-slate-500">Tempo habitual até adormecer.</div>
            </div>
          </div>
      </QuestionnaireResultHero>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Componente</th>
              <th className="px-6 py-4">Score</th>
              <th className="px-6 py-4">Como foi resumido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {PSQI_COMPONENT_METADATA.map((item) => (
              <tr key={item.key} className="hover:bg-slate-50/60">
                <td className="px-6 py-4">
                  <div className="font-medium text-slate-800">{item.label}</div>
                  <div className="mt-1 text-xs text-slate-500">{item.description}</div>
                </td>
                <td className="px-6 py-4 text-center font-black text-slate-900">{normalized.components[item.key]}/3</td>
                <td className="px-6 py-4 text-slate-700">{getComponentDetail(normalized, item.key)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
          <div className="bg-slate-50 px-6 py-4 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Resumo das respostas principais</div>
          <div className="divide-y divide-slate-100 bg-white text-sm">
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Deitar</div>
              <div className="text-slate-800">{normalized.bedtime}</div>
            </div>
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Levantar</div>
              <div className="text-slate-800">{normalized.wakeTime}</div>
            </div>
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Horas dormidas</div>
              <div className="text-slate-800">{formatHourValue(normalized.sleepDurationHours)}</div>
            </div>
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Qualidade subjetiva</div>
              <div className="text-slate-800">{getOptionLabel(PSQI_SUBJECTIVE_QUALITY_OPTIONS, normalized.subjectiveQuality)}</div>
            </div>
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Medicação para dormir</div>
              <div className="text-slate-800">{getOptionLabel(PSQI_FREQUENCY_OPTIONS, normalized.sleepMedicationUse)}</div>
            </div>
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Ficar acordado</div>
              <div className="text-slate-800">{getOptionLabel(PSQI_FREQUENCY_OPTIONS, normalized.daytimeSleepiness)}</div>
            </div>
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-4 px-6 py-4">
              <div className="font-semibold text-slate-600">Entusiasmo</div>
              <div className="text-slate-800">{getOptionLabel(PSQI_DAYTIME_PROBLEM_OPTIONS, normalized.daytimeEnthusiasmProblem)}</div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
          <div className="bg-slate-50 px-6 py-4 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Componentes mais afetados</div>
          <div className="divide-y divide-slate-100 bg-white">
            {topComponents.length > 0 ? (
              topComponents.map((item) => (
                <div key={item.key} className="px-6 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-slate-800">{item.label}</div>
                    <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">{item.score}/3</div>
                  </div>
                  <div className="mt-2 text-sm text-slate-600">{getComponentDetail(normalized, item.key)}</div>
                </div>
              ))
            ) : (
              <div className="px-6 py-6 text-sm text-slate-500">Nenhum componente apresentou alteração relevante.</div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Distúrbio noturno</th>
              <th className="px-6 py-4">Frequência</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {PSQI_DISTURBANCE_ITEMS.map((item) => (
              <tr key={item.key} className="hover:bg-slate-50/60">
                <td className="px-6 py-4 font-medium text-slate-700">{item.label}</td>
                <td className="px-6 py-4 text-slate-700">{getOptionLabel(PSQI_FREQUENCY_OPTIONS, normalized.disturbances[item.key])}</td>
              </tr>
            ))}
            <tr className="hover:bg-slate-50/60">
              <td className="px-6 py-4 font-medium text-slate-700">Descrição da outra razão</td>
              <td className="px-6 py-4 text-slate-700">{normalized.disturbances.otherReasonText || 'Não informado'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <h4 className="text-xl font-black tracking-tight text-slate-800">Parceiro(a) ou colega de quarto</h4>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Situação informada: {getOptionLabel(PSQI_PARTNER_STATUS_OPTIONS, normalized.partnerStatus)}.
          {' '}
          Esses dados complementares não entram no score global.
        </p>

        {hasPartnerNotes ? (
          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
                  <th className="px-6 py-4">Observação</th>
                  <th className="px-6 py-4">Frequência</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {PSQI_PARTNER_OBSERVATION_ITEMS.map((item) => (
                  <tr key={item.key} className="hover:bg-slate-50/60">
                    <td className="px-6 py-4 font-medium text-slate-700">{item.label}</td>
                    <td className="px-6 py-4 text-slate-700">
                      {normalized.partnerObservations[item.key] == null
                        ? 'Não informado'
                        : getOptionLabel(PSQI_FREQUENCY_OPTIONS, normalized.partnerObservations[item.key])}
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-medium text-slate-700">Descrição adicional</td>
                  <td className="px-6 py-4 text-slate-700">{normalized.partnerObservations.otherRestlessnessText || 'Não informado'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
            Não foram registrados achados complementares do parceiro(a) ou colega de quarto.
          </div>
        )}
      </div>
    </div>
  );
}
